import { useState } from 'react';
import { Plus, Trash2, Phone, Mail, X, Search, MessageSquare, User, Clock, Send, MoreVertical, Filter } from 'lucide-react';
import { BackHeader } from '../components/BackHeader';

interface Message {
  id: string;
  guestName: string;
  preview: string;
  phone: string;
  email: string;
  platform: 'airbnb' | 'booking' | 'direct' | 'whatsapp';
  unread: boolean;
  timestamp: string;
  avatar?: string;
}

const platforms = {
  airbnb: { label: 'Airbnb', color: 'bg-rose-500', text: 'text-rose-600', bg: 'bg-rose-50' },
  booking: { label: 'Booking.com', color: 'bg-blue-600', text: 'text-blue-600', bg: 'bg-blue-50' },
  direct: { label: 'Directo', color: 'bg-emerald-500', text: 'text-emerald-600', bg: 'bg-emerald-50' },
  whatsapp: { label: 'WhatsApp', color: 'bg-green-500', text: 'text-green-600', bg: 'bg-green-50' },
};

export default function GuestCommunication() {
  const [messages, setMessages] = useState<Message[]>([
    { 
      id: '1', 
      guestName: 'María González', 
      preview: 'Hola, ¿a qué hora puedo hacer el check-in mañana?', 
      phone: '', 
      email: '',
      platform: 'airbnb',
      unread: true,
      timestamp: 'Hace 5 min'
    },
    { 
      id: '2', 
      guestName: 'John Smith', 
      preview: 'Thanks for the quick response! See you tomorrow.', 
      phone: '', 
      email: '',
      platform: 'booking',
      unread: false,
      timestamp: 'Hace 2 horas'
    },
    { 
      id: '3', 
      guestName: 'Ana López', 
      preview: '¿Hay estacionamiento disponible en el edificio?', 
      phone: '', 
      email: '',
      platform: 'whatsapp',
      unread: true,
      timestamp: 'Hace 3 horas'
    },
    { 
      id: '4', 
      guestName: 'Carlos Mendoza', 
      preview: 'Confirmo mi reserva para el próximo fin de semana.', 
      phone: '', 
      email: '',
      platform: 'direct',
      unread: false,
      timestamp: 'Ayer'
    },
  ]);
  
  const [selected, setSelected] = useState<Message | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyText, setReplyText] = useState('');
  const [filterPlatform, setFilterPlatform] = useState<string>('all');

  const filteredMessages = messages.filter(m => {
    const matchesSearch = m.guestName.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         m.preview.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPlatform = filterPlatform === 'all' || m.platform === filterPlatform;
    return matchesSearch && matchesPlatform;
  });

  const deleteMessage = (id: string) => {
    if (confirm('¿Eliminar esta conversación?')) {
      setMessages(messages.filter(m => m.id !== id));
      if (selected?.id === id) setSelected(null);
    }
  };

  const markAsRead = (id: string) => {
    setMessages(messages.map(m => m.id === id ? { ...m, unread: false } : m));
  };

  const sendReply = () => {
    if (!replyText.trim() || !selected) return;
    alert(`Mensaje enviado a ${selected.guestName}`);
    setReplyText('');
  };

  return (
    <div className="h-full flex flex-col">
      <BackHeader fallback="/" />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Mensajes</h2>
          <p className="text-slate-500 mt-1">Comunicación unificada con huéspedes</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Buscar mensajes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
            />
          </div>
          <button 
            onClick={() => setShowNew(true)} 
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all hover:shadow-lg hover:shadow-blue-600/25"
          >
            <Plus className="w-4 h-4" /> Nuevo Mensaje
          </button>
        </div>
      </div>

      {/* Filter Pills */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button 
          onClick={() => setFilterPlatform('all')}
          className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
            filterPlatform === 'all' 
              ? 'bg-[var(--v-surface-1)] text-white' 
              : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
          }`}
        >
          Todos
        </button>
        {Object.entries(platforms).map(([key, platform]) => (
          <button 
            key={key}
            onClick={() => setFilterPlatform(key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
              filterPlatform === key 
                ? `${platform.bg} ${platform.text} border border-current` 
                : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
            }`}
          >
            {platform.label}
          </button>
        ))}
      </div>

      {/* Messages List */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1">
        {/* Message List */}
        <div className="lg:col-span-1 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <h3 className="font-semibold text-slate-900">Conversaciones</h3>
            <p className="text-sm text-slate-500">{filteredMessages.length} mensajes</p>
          </div>
          <div className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
            {filteredMessages.length === 0 ? (
              <div className="p-8 text-center">
                <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No hay mensajes</p>
              </div>
            ) : (
              filteredMessages.map(m => {
                const platform = platforms[m.platform];
                return (
                  <div 
                    key={m.id} 
                    onClick={() => { setSelected(m); markAsRead(m.id); }}
                    className={`p-4 cursor-pointer transition-all hover:bg-slate-50 ${
                      selected?.id === m.id ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                    } ${m.unread ? 'bg-slate-50/50' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${platform.color}`}>
                        {m.guestName.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className={`text-sm truncate ${m.unread ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                            {m.guestName}
                          </h4>
                          {m.unread && <span className="w-2 h-2 bg-blue-600 rounded-full"></span>}
                        </div>
                        <p className={`text-sm truncate ${m.unread ? 'text-slate-700' : 'text-slate-500'}`}>
                          {m.preview}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`text-xs px-2 py-0.5 rounded-full ${platform.bg} ${platform.text}`}>
                            {platform.label}
                          </span>
                          <span className="text-xs text-slate-400">{m.timestamp}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Message Detail / Conversation */}
        <div className="lg:col-span-2">
          {selected ? (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full flex flex-col">
              {/* Conversation Header */}
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold ${platforms[selected.platform].color}`}>
                    {selected.guestName.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{selected.guestName}</h3>
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${platforms[selected.platform].bg} ${platforms[selected.platform].text}`}>
                        {platforms[selected.platform].label}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {selected.phone && (
                  <a 
                    href={`tel:${selected.phone}`} 
                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Llamar"
                  >
                    <Phone className="w-5 h-5" />
                  </a>
                  )}
                  {selected.email && (
                  <a 
                    href={`mailto:${selected.email}`} 
                    className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="Enviar email"
                  >
                    <Mail className="w-5 h-5" />
                  </a>
                  )}
                  <button 
                    onClick={() => deleteMessage(selected.id)}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 p-4 space-y-4 overflow-y-auto max-h-[400px]">
                {/* Guest Message */}
                <div className="flex gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold ${platforms[selected.platform].color}`}>
                    {selected.guestName.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <div className="bg-slate-100 rounded-2xl rounded-tl-none p-4 max-w-[80%]">
                      <p className="text-slate-700">{selected.preview}</p>
                    </div>
                    <span className="text-xs text-slate-400 mt-1 ml-1">{selected.timestamp}</span>
                  </div>
                </div>

                {/* System/Auto Response */}
                <div className="flex gap-3 justify-end">
                  <div className="flex-1 flex flex-col items-end">
                    <div className="bg-blue-600 text-white rounded-2xl rounded-tr-none p-4 max-w-[80%]">
                      <p>¡Hola! Gracias por contactarnos. Te responderemos a la brevedad.</p>
                    </div>
                    <span className="text-xs text-slate-400 mt-1 mr-1">Automático</span>
                  </div>
                </div>
              </div>

              {/* Reply Input */}
              <div className="p-4 border-t border-slate-200">
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Escribe tu respuesta..."
                    className="flex-1 px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    onKeyPress={(e) => e.key === 'Enter' && sendReply()}
                  />
                  <button 
                    onClick={sendReply}
                    disabled={!replyText.trim()}
                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-all hover:shadow-lg hover:shadow-blue-600/25 flex items-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Enviar
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full flex items-center justify-center">
              <div className="text-center p-8">
                <MessageSquare className="w-16 h-16 text-slate-200 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Selecciona una conversación</h3>
                <p className="text-slate-500">Haz clic en un mensaje para ver la conversación completa</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* New Message Modal */}
      {showNew && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-slate-900">Nuevo Mensaje</h3>
              <button 
                onClick={() => setShowNew(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Nombre del huésped</label>
                <div className="relative">
                  <User className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input 
                    type="text" 
                    className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ej: María González"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Teléfono</label>
                  <div className="relative">
                    <Phone className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input 
                      type="tel" 
                      className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="+52..."
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <div className="relative">
                    <Mail className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input 
                      type="email" 
                      className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="correo@ejemplo.com"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Mensaje</label>
                <textarea 
                  rows={4}
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Escribe tu mensaje..."
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button 
                  type="button"
                  onClick={() => setShowNew(false)}
                  className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Enviar Mensaje
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
